import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../components/Badge';
import { apiFetch } from '../../api/client';

/**
 * Phase 7-C — Verification Engine.
 *
 * Now consumes the PEx v2 evaluation engine (`POST /presentations/evaluate`)
 * and renders the **per-input-descriptor outcomes** alongside the legacy
 * top-level checks. Architecture §4.2, §6 — never short-circuits; the body
 * always carries the full `descriptorOutcomes` array.
 */

interface CheckResult {
  label: string;
  icon: string;
  status: 'pass' | 'fail' | 'pending';
  detail: string;
}

interface DescriptorOutcomeWire {
  descriptorId: string;
  satisfied: boolean;
  credentialId?: string | null;
  format?: string | null;
  revealedFields?: string[];
  hiddenFields?: string[];
  predicateResults?: Record<string, boolean>;
  failureReason?:
    | 'FORMAT_MISMATCH'
    | 'SIGNATURE_INVALID'
    | 'ISSUER_UNTRUSTED'
    | 'SCHEMA_MISMATCH'
    | 'REVOKED'
    | 'CONSTRAINT_NOT_SATISFIED'
    | 'SCHEMA_VERSION_MISMATCH'
    | null;
}

interface GroupOutcomeWire {
  name?: string;
  rule: 'all' | 'pick';
  from?: string | null;
  satisfied: boolean;
  matched: number;
  count?: number | null;
  min?: number | null;
  max?: number | null;
}

interface EvaluationOutcomeWire {
  evaluationId?: string;
  presentationDefinitionId?: string;
  overallSatisfied?: boolean;
  presentationProofValid?: boolean;
  challengeMatched?: boolean;
  domainMatched?: boolean;
  descriptorOutcomes?: DescriptorOutcomeWire[];
  groupOutcomes?: GroupOutcomeWire[];
  errors?: string[];
}

export default function VerificationEngine() {
  const { t } = useTranslation('verifier');
  const [vpJson, setVpJson] = useState('');
  const [pdId, setPdId] = useState('');
  const [submissionJson, setSubmissionJson] = useState('');
  const [challenge, setChallenge] = useState('');
  const [domain, setDomain] = useState('');
  const [outcome, setOutcome] = useState<EvaluationOutcomeWire | null>(null);
  const [legacyChecks, setLegacyChecks] = useState<CheckResult[]>([]);
  const [verdict, setVerdict] = useState<'verified' | 'rejected' | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleVerify = async () => {
    setVerifying(true);
    setVerdict(null);
    setOutcome(null);
    setErrorMsg(null);
    try {
      const vpData = JSON.parse(vpJson);

      // PEx v2 path: when caller supplies a PD id, route to `/presentations/evaluate`.
      if (pdId.trim()) {
        let submission: Record<string, unknown> | undefined;
        if (submissionJson.trim()) {
          try {
            submission = JSON.parse(submissionJson);
          } catch {
            setErrorMsg(t('pex.engine.submissionParseError'));
            setVerifying(false);
            return;
          }
        }
        const result = await apiFetch<EvaluationOutcomeWire>('/presentations/evaluate', {
          method: 'POST',
          body: JSON.stringify({
            presentationDefinitionId: pdId.trim(),
            presentation: vpData,
            presentationSubmission: submission,
            challenge: challenge || undefined,
            domain: domain || undefined,
          }),
        });
        setOutcome(result);
        setVerdict(result.overallSatisfied ? 'verified' : 'rejected');
        setLegacyChecks(deriveTopLevelChecks(result, t));
      } else {
        // Legacy single-verdict path.
        const result = await apiFetch<{
          valid?: boolean;
          verified?: boolean;
          checks?: CheckResult[];
          descriptorOutcomes?: DescriptorOutcomeWire[];
          errors?: string[];
        }>('/presentations/verify', {
          method: 'POST',
          body: JSON.stringify({ presentation: vpData, challenge: challenge || undefined, domain: domain || undefined }),
        });
        const ok = result.valid ?? result.verified ?? false;
        setVerdict(ok ? 'verified' : 'rejected');
        setLegacyChecks(result.checks ?? []);
        if (result.descriptorOutcomes) {
          setOutcome({ overallSatisfied: ok, descriptorOutcomes: result.descriptorOutcomes });
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErrorMsg(msg);
      setVerdict('rejected');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('verifyEngine.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('verifyEngine.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
            <h2 className="mb-3 text-sm font-extrabold uppercase tracking-widest text-text-main">{t('pex.engine.bindings')}</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-bold text-text-muted">{t('pex.engine.pdId')}</label>
                <input className="w-full rounded-lg border border-border-stroke bg-white px-3 py-2 font-mono text-xs text-text-main" placeholder="pd_..." value={pdId} onChange={(e) => setPdId(e.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold text-text-muted">{t('pex.engine.challenge')}</label>
                <input className="w-full rounded-lg border border-border-stroke bg-white px-3 py-2 font-mono text-xs text-text-main" placeholder="z..." value={challenge} onChange={(e) => setChallenge(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1.5 block text-xs font-bold text-text-muted">{t('pex.engine.domain')}</label>
                <input className="w-full rounded-lg border border-border-stroke bg-white px-3 py-2 font-mono text-xs text-text-main" placeholder="https://verifier.example/verify" value={domain} onChange={(e) => setDomain(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
            <h2 className="mb-3 text-sm font-extrabold uppercase tracking-widest text-text-main">{t('verifyEngine.vp')}</h2>
            <textarea
              className="h-56 w-full rounded-lg border border-border-stroke bg-white p-4 font-mono text-xs text-text-main placeholder:text-text-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder={t('verifyEngine.vpPlaceholder')}
              value={vpJson}
              onChange={(e) => { setVpJson(e.target.value); setLegacyChecks([]); setOutcome(null); setVerdict(null); }}
            />
            <div className="mt-3">
              <label className="mb-1.5 block text-xs font-bold text-text-muted">{t('pex.engine.submission')}</label>
              <textarea
                className="h-24 w-full rounded-lg border border-border-stroke bg-white p-3 font-mono text-[11px] text-text-main"
                placeholder='{"id":"sub_1","definition_id":"pd_…","descriptor_map":[{"id":"diploma","format":"ldp_vc","path":"$.verifiableCredential[0]"}]}'
                value={submissionJson}
                onChange={(e) => setSubmissionJson(e.target.value)}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <button className="btn-primary" onClick={handleVerify} disabled={!vpJson.trim() || verifying}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>fact_check</span>
                {verifying ? t('verifyEngine.verifying') : t('verifyEngine.verify')}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {verdict && (
            <div
              className={`rounded-xl border-2 p-6 text-center ${
                verdict === 'verified' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
              }`}
            >
              <span
                className={`material-symbols-outlined ${verdict === 'verified' ? 'text-emerald-500' : 'text-red-500'}`}
                style={{ fontSize: 48 }}
              >
                {verdict === 'verified' ? 'verified' : 'gpp_bad'}
              </span>
              <h2 className={`mt-2 text-xl font-bold ${verdict === 'verified' ? 'text-emerald-700' : 'text-red-700'}`}>
                {verdict === 'verified' ? t('verifyEngine.verifiedTitle') : t('verifyEngine.rejectedTitle')}
              </h2>
              <p className="mt-1 text-xs text-text-muted">
                {verdict === 'verified' ? t('verifyEngine.verifiedDetail') : t('verifyEngine.rejectedDetail')}
              </p>
            </div>
          )}

          {errorMsg && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">{errorMsg}</div>
          )}

          {legacyChecks.length > 0 && <ChecksPanel checks={legacyChecks} t={t} />}

          {outcome?.descriptorOutcomes && outcome.descriptorOutcomes.length > 0 && (
            <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-sm font-extrabold uppercase tracking-widest text-text-main">
                {t('pex.engine.perDescriptor')}
              </h2>
              <div className="space-y-2">
                {outcome.descriptorOutcomes.map((d) => (
                  <DescriptorCard
                    key={d.descriptorId}
                    outcome={d}
                    expanded={expanded.has(d.descriptorId)}
                    onToggle={() => toggleExpand(d.descriptorId)}
                    t={t}
                  />
                ))}
              </div>
            </div>
          )}

          {outcome?.groupOutcomes && outcome.groupOutcomes.length > 0 && (
            <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-sm font-extrabold uppercase tracking-widest text-text-main">
                {t('pex.engine.submissionRequirements')}
              </h2>
              <div className="space-y-2">
                {outcome.groupOutcomes.map((g, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-lg border border-border-subtle bg-slate-50/50 p-3">
                    <Badge variant={g.satisfied ? 'success' : 'error'}>{g.satisfied ? 'pass' : 'fail'}</Badge>
                    <div className="flex-1 text-xs">
                      <div className="font-bold text-text-main">{g.name ?? `${g.rule}${g.from ? ` from ${g.from}` : ''}`}</div>
                      <div className="text-text-muted">
                        rule={g.rule}
                        {g.from && ` · from=${g.from}`}
                        {g.count != null && ` · count=${g.count}`}
                        {g.min != null && ` · min=${g.min}`}
                        {g.max != null && ` · max=${g.max}`}
                        {' · '}matched={g.matched}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {legacyChecks.length === 0 && !outcome && !verdict && (
            <div className="flex h-64 items-center justify-center rounded-xl border border-border-subtle bg-white p-6 text-center shadow-sm">
              <div>
                <span className="material-symbols-outlined mb-2 text-text-faint" style={{ fontSize: 40 }}>fact_check</span>
                <p className="text-sm text-text-faint">{t('verifyEngine.emptyHint')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChecksPanel({ checks, t }: { checks: CheckResult[]; t: (key: string) => string }) {
  return (
    <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-sm font-extrabold uppercase tracking-widest text-text-main">{t('verifyEngine.checks')}</h2>
      <div className="space-y-3">
        {checks.map((check) => (
          <div
            key={check.label}
            className={`flex items-start gap-3 rounded-lg border p-4 ${
              check.status === 'pass'
                ? 'border-emerald-100 bg-emerald-50/50'
                : check.status === 'fail'
                ? 'border-red-100 bg-red-50/50'
                : 'border-border-subtle bg-slate-50'
            }`}
          >
            <div className="text-sm">
              <div className="font-bold text-text-main">{check.label}</div>
              <div className="mt-0.5 text-xs text-text-muted">{check.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DescriptorCard({
  outcome,
  expanded,
  onToggle,
  t,
}: {
  outcome: DescriptorOutcomeWire;
  expanded: boolean;
  onToggle: () => void;
  t: (key: string) => string;
}) {
  const reason = outcome.failureReason;
  return (
    <div className="rounded-lg border border-border-subtle bg-white">
      <button onClick={onToggle} className="flex w-full items-center gap-3 p-3 text-left hover:bg-slate-50">
        <Badge variant={outcome.satisfied ? 'success' : 'error'}>
          {outcome.satisfied ? '✓' : '✗'}
        </Badge>
        <span className="flex-1 font-mono text-xs font-bold text-text-main">{outcome.descriptorId}</span>
        {reason && <span className="text-[10px] font-bold uppercase tracking-wide text-red-600">{t(`pex.failureReasons.${reason}`)}</span>}
        <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 18 }}>
          {expanded ? 'expand_less' : 'expand_more'}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-border-subtle p-3 text-xs">
          {outcome.credentialId && (
            <div className="flex items-start gap-2 py-1">
              <span className="text-text-faint">{t('pex.descriptorOutcome.credential')}</span>
              <span className="font-mono text-text-main">{outcome.credentialId}</span>
            </div>
          )}
          {outcome.format && (
            <div className="flex items-start gap-2 py-1">
              <span className="text-text-faint">{t('pex.descriptorOutcome.format')}</span>
              <span className="font-mono text-text-main">{outcome.format}</span>
            </div>
          )}
          {outcome.revealedFields && outcome.revealedFields.length > 0 && (
            <div className="py-1">
              <div className="text-text-faint">{t('pex.descriptorOutcome.revealed')}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {outcome.revealedFields.map((f) => (
                  <span key={f} className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] text-blue-700">{f}</span>
                ))}
              </div>
            </div>
          )}
          {outcome.hiddenFields && outcome.hiddenFields.length > 0 && (
            <div className="py-1">
              <div className="text-text-faint">{t('pex.descriptorOutcome.hidden')}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {outcome.hiddenFields.map((f) => (
                  <span key={f} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">{f}</span>
                ))}
              </div>
            </div>
          )}
          {outcome.predicateResults && Object.keys(outcome.predicateResults).length > 0 && (
            <div className="py-1">
              <div className="text-text-faint">{t('pex.descriptorOutcome.predicates')}</div>
              <div className="mt-1 space-y-0.5">
                {Object.entries(outcome.predicateResults).map(([k, v]) => (
                  <div key={k} className="font-mono text-[10px] text-text-main">
                    {k}: {v ? '✓' : '✗'}
                  </div>
                ))}
              </div>
            </div>
          )}
          {reason && (
            <div className="mt-2 rounded border border-red-100 bg-red-50 p-2 text-[11px] text-red-700">
              {t(`pex.failureReasons.${reason}`)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function deriveTopLevelChecks(out: EvaluationOutcomeWire, t: (k: string) => string): CheckResult[] {
  const mk = (key: string, ok: boolean, label: string): CheckResult => ({
    label,
    icon: 'check',
    status: ok ? 'pass' : 'fail',
    detail: ok ? t('pex.engine.passed') : t(`pex.engine.failed.${key}`),
  });
  return [
    mk('proof', !!out.presentationProofValid, t('pex.engine.checks.proof')),
    mk('challenge', !!out.challengeMatched, t('pex.engine.checks.challenge')),
    mk('domain', !!out.domainMatched, t('pex.engine.checks.domain')),
  ];
}
